import React, { useState } from "react";
import { Star } from "lucide-react";
import config from "../../../config";
import { useNavigate } from "react-router-dom";
import AuthPopup from "../../home/authComponents/AuthPopup";
import { AddToCartButton } from "../../../components/home/homePageComponents/ButtonComponents";
import { useSelector } from "react-redux";
import { AddToWishlistButton } from "../../../components/home/homePageComponents/ButtonComponents";
import { RentNowButton } from "./RentNowButton";
import RatingStars from "./RatingStars";

const BookCard = ({ book }) => {//child component for displaying individual book details
  const {
    image,
    title,
    author,
    isbn,
    rating,
    id: bookId,
    inStock: bookStockStatus,
  } = book;
  const [hovered, setHovered] = useState(false);
  const [message, setMessage] = useState("");
  const [showAuthPopup, setShowAuthPopup] = useState(false);

  const navigate = useNavigate();
  const userId = useSelector((state) => state.auth.user?.userId);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const defaultGradient = "linear-gradient(155deg, #FFFFFF 39.9%, #F5F5F5 40%)";
  const hoverGradient = "linear-gradient(155deg, #FFFFFF 39.9%, #FFD8C7 40%)";
  return (
    <div
      className="relative group w-40 sm:w-44 md:w-48 rounded-lg p-1 sm:p-2 border border-gray-200 flex flex-col items-center shadow-md transition-all duration-300 ease-in-out transform hover:shadow-xl"
      style={{ background: hovered ? hoverGradient : defaultGradient }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Wishlist Heart Icon */}
      <div className="absolute top-1 right-1 sm:top-1 md:right-2 sm:right-0 z-10">
        <AddToWishlistButton book={book} />
      </div>

      <div className="mb-1 sm:mb-2 relative z-10 book-group">
        <div
          className="p-1 border border-gray-200 shadow-sm relative overflow-hidden bg-gray-100"
          style={{ minHeight: 170 }} // Reserve space before image paints
          onClick={() => navigate(`/book/${bookId}`)}
        >
          <img
            src={
              image && image !== "defaultImage.jpg"
                ? `${config.url}${image}`
                : "/images/defaultBook.png"
            }
            alt={title}
            loading="lazy"
            decoding="async"
            onLoad={(e) => e.currentTarget.classList.remove("opacity-0")}
            onError={(e) => e.currentTarget.classList.remove("opacity-0")}
            className="w-24 h-32 md:w-28 sm:w-24 sm:h-40 object-cover cursor-pointer transition-opacity duration-300 opacity-0"
          />

          {/* Animated border overlays */}
          <span className="border-anim top-border"></span>
          <span className="border-anim right-border"></span>
          <span className="border-anim bottom-border"></span>
          <span className="border-anim left-border"></span>
        </div>

        <style jsx>{`
          .border-anim {
            position: absolute;
            background-color: #ff7236;
            transition: all 200ms ease-in-out;
          }
          .top-border {
            top: 0;
            left: 50%;
            height: 1px;
            width: 0;
            transform: translateX(-50%);
          }
          .right-border {
            right: 0;
            top: 50%;
            width: 1px;
            height: 0;
            transform: translateY(-50%);
          }
          .bottom-border {
            bottom: 0;
            left: 50%;
            height: 1px;
            width: 0;
            transform: translateX(-50%);
          }
          .left-border {
            left: 0;
            top: 50%;
            width: 1px;
            height: 0;
            transform: translateY(-50%);
          }
          .book-group:hover .top-border,
          .book-group:hover .bottom-border {
            width: 100%;
          }
          .book-group:hover .right-border,
          .book-group:hover .left-border {
            height: 100%;
          }
        `}</style>
      </div>

      {/* Book Details */}
      <div className="z-10 text-center px-1 sm:px-1.5 w-full overflow-hidden">
        <h3 className="text-xs sm:text-sm font-medium text-black w-full truncate">
          {title}
        </h3>
        <p className="text-[10px] sm:text-xs text-gray-700 mt-1 w-full truncate">
          <span className="font-semibold">By:</span> {author}
        </p>

        {/* Rating */}
        <div className="mt-1 mb-1.5">
          <RatingStars rating={rating} size={16} />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="z-10 flex justify-between w-full gap-1 px-1 sm:px-2 mt-auto">
        <div className="flex-1">
          <RentNowButton book={book} className="text-xs sm:text-[10px]" />
        </div>
        {/* DRY out your Add to Cart logic */}
        <div className="flex-1">
          <AddToCartButton
            book={book}
            className="text-xs sm:text-[10px] h-8"
            labelClassName="text-xs sm:text-[10px] h-16 items-center"
          />
        </div>
      </div>

      {/* Message on unsuccessful login */}
      {message && (
        <div className="absolute bottom-0 left-0 right-0 bg-red-500 text-white text-xs text-center py-1">
          {message}
        </div>
      )}

      <AuthPopup
        isOpen={showAuthPopup}
        onClose={() => setShowAuthPopup(false)}
      />
    </div>
  );
};

export default BookCard;
